// Export utilities for diamond data
/**
 * Convert array of objects to CSV string
 */
function convertToCSV(data) {
    if (!data || data.length === 0) {
        return '';
    }
    // Get all unique keys from all objects
    const allKeys = new Set();
    data.forEach(item => {
        Object.keys(item).forEach(key => allKeys.add(key));
    });
    const headers = Array.from(allKeys);
    // Create header row
    const csvRows = [];
    csvRows.push(headers.map(header => `"${header}"`).join(','));
    // Create data rows
    data.forEach(item => {
        const values = headers.map(header => {
            const value = item[header];
            // Handle different value types
            if (value === null || value === undefined) {
                return '""';
            }
            if (typeof value === 'object') {
                return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
            }
            const stringValue = String(value).replace(/"/g, '""');
            return `"${stringValue}"`;
        });
        csvRows.push(values.join(','));
    });
    return csvRows.join('\n');
}
/**
 * Download data as CSV file
 */
export function exportToCSV(data, filename = 'diamonds-export') {
    try {
        const csv = convertToCSV(data);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename}-${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        return true;
    }
    catch (error) {
        console.error('CSV export failed:', error);
        return false;
    }
}
/**
 * Download data as JSON file
 */
export function exportToJSON(data, filename = 'diamonds-export') {
    try {
        const json = JSON.stringify(data, null, 2);
        const blob = new Blob([json], { type: 'application/json;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename}-${new Date().toISOString().slice(0, 10)}.json`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        return true;
    }
    catch (error) {
        console.error('JSON export failed:', error);
        return false;
    }
}
/**
 * Copy data to clipboard as JSON
 */
export async function copyToClipboard(data) {
    try {
        const json = JSON.stringify(data, null, 2);
        await navigator.clipboard.writeText(json);
        return true;
    }
    catch (error) {
        console.error('Clipboard copy failed:', error);
        return false;
    }
}
/**
 * Prepare diamond data for export (remove sensitive/internal fields)
 */
export function prepareDiamondsForExport(diamonds, includeMetadata = false) {
    return diamonds.map(diamond => {
        const exported = {
            // Core specifications
            stockNumber: diamond.stockNumber,
            shape: diamond.shape,
            shapeCode: diamond.shapeCode,
            caratWeight: diamond.caratWeight,
            color: diamond.color,
            clarity: diamond.clarity,
            cut: diamond.cut,
            polish: diamond.polish,
            symmetry: diamond.symmetry,
            fluorescence: diamond.fluorescence,
            fluorescenceColor: diamond.fluorescenceColor,
            // Pricing
            pricePerCarat: diamond.pricePerCarat,
            totalPrice: diamond.totalPrice,
            priceDisplay: diamond.priceDisplay,
            cashPrice: diamond.cashPrice,
            // Measurements
            measurements: diamond.measurements,
            depthPercent: diamond.depthPercent,
            tablePercent: diamond.tablePercent,
            // Girdle & Culet
            girdleThin: diamond.girdleThin,
            girdleThick: diamond.girdleThick,
            culetSize: diamond.culetSize,
            culetCondition: diamond.culetCondition,
            // Crown & Pavilion
            crownAngle: diamond.crownAngle,
            pavilionAngle: diamond.pavilionAngle,
            // Certification
            lab: diamond.lab,
            certificateNumber: diamond.certificateNumber,
            certificateImageFilename: diamond.certificateImageFilename,
            // Images
            imageFilename: diamond.imageFilename,
            // Stock & Inventory
            pairStockNumber: diamond.pairStockNumber,
            isMatchedPairSeparable: diamond.isMatchedPairSeparable,
            // Fancy color
            fancyColor: diamond.fancyColor,
            fancyColorIntensity: diamond.fancyColorIntensity,
            fancyColorOvertone: diamond.fancyColorOvertone,
            // Additional attributes
            treatment: diamond.treatment,
            comments: diamond.comments,
            // Location
            city: diamond.city,
            state: diamond.state,
            country: diamond.country,
            // Inclusions
            milky: diamond.milky,
            shade: diamond.shade,
            eyeClean: diamond.eyeClean,
            // Branding
            brand: diamond.brand,
            laserInscription: diamond.laserInscription,
            // Business fields
            status: diamond.status,
            featured: diamond.featured,
            certified: diamond.certified,
        };
        // Optionally include metadata
        if (includeMetadata) {
            exported.id = diamond.id;
            exported.source = diamond.source;
            exported.importedAt = diamond.importedAt;
            exported.lastUpdated = diamond.lastUpdated;
        }
        // Remove undefined/null values
        Object.keys(exported).forEach(key => {
            if (exported[key] === undefined || exported[key] === null || exported[key] === '') {
                delete exported[key];
            }
        });
        return exported;
    });
}
